<template>
  <div class="home-draft-root bg-white text-[#1d1d1f]">

    <!-- ═══════════════════════════════ NAV ═══════════════════════════════ -->
    <header class="adme-nav">
      <div class="max-w-6xl mx-auto px-5 sm:px-8 py-3 flex items-center justify-between">
        <a href="#top" class="flex items-center gap-3 min-w-0">
          <img src="/ADME_logo.png" alt="ADME" class="hidden xs:inline-block h-5 sm:h-6 flex-shrink-0" />
          <span class="hidden xs:inline-block w-px h-5 sm:h-6 bg-[#d2d2d7] flex-shrink-0" aria-hidden="true"></span>
          <img :src="logoURLBlack" alt="Engagr" class="h-8 sm:h-10 flex-shrink-0" />
          <span class="hidden md:inline text-[13px] text-[#6e6e73] font-normal pl-1">Customer welcome portal for automotive retail</span>
        </a>
        <nav class="hidden md:flex items-center gap-1">
          <a href="#watch" class="adme-nav-link">Watch</a>
          <a href="#how-it-works" class="adme-nav-link">How It Works</a>
          <a href="#tracking" class="adme-nav-link">Tracking</a>
          <a href="#dashboard" class="adme-nav-link">Dashboard</a>
        </nav>
        <a href="#contact" class="adme-btn adme-btn-primary text-[13px]">Request a Demo</a>
      </div>
    </header>

    <!-- ═══════════════════════════════ HERO (dark, matches /dashboard) ═══════════════════════════════ -->
    <section id="top" class="relative overflow-hidden" style="background: #0a0a0c;">
      <!-- Background photo + overlays — same recipe as /dashboard -->
      <div class="absolute inset-0 z-0 pointer-events-none">
        <img src="/assets/photo.jpg" alt="" class="w-full h-full object-cover opacity-50 bg-image-animate" />
      </div>
      <div class="absolute inset-0 z-0 pointer-events-none bg-gradient-to-br from-blue-900/70 via-blue-900/70 to-gray-900/70 mix-blend-multiply"></div>
      <div class="absolute inset-0 z-0 pointer-events-none bg-black/40 backdrop-blur-[2px]"></div>

      <div class="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pt-28 sm:pt-36 pb-20 sm:pb-28 text-center">
        <div class="adme-chip adme-chip-dark mb-7">
          <span class="adme-dot adme-dot-emerald mr-2"></span>
          <span>Built for dealer groups by</span>
          <a href="https://adme.net.au" target="_blank" rel="noopener noreferrer"
             class="ml-1.5 text-blue-300 hover:text-blue-200 hover:underline font-medium">ADME</a>
        </div>

        <h1 class="adme-display adme-display-light text-5xl sm:text-6xl md:text-7xl mb-4 max-w-4xl mx-auto">
          Capture the Delivery Moment
        </h1>

        <p class="adme-display adme-display-light text-2xl sm:text-3xl md:text-4xl mb-6 max-w-3xl mx-auto text-white/90">
          Turn It Into Reviews, Reach, Repeat Revenue
        </p>

        <p class="text-lg sm:text-xl mb-10 max-w-2xl mx-auto text-white/80">
          Capture handover photos, customer stories, and consent directly from the showroom floor —
          then instantly distribute them via email and SMS, with full delivery tracking in your dashboard.
        </p>

        <div class="flex flex-col sm:flex-row items-center justify-center gap-3">
          <a href="#contact" class="adme-btn adme-btn-primary">
            Request a Demo
            <svg class="ml-2 w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </a>
          <a href="#how-it-works" class="adme-btn adme-btn-link text-white hover:text-white/80">
            See How It Works
            <svg class="ml-1 w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════════ DELIVERY PHOTOS MARQUEE (white) ═══════════════════════════════ -->
    <!-- Heading always renders; the photo grid below quietly disappears if there
         are no photos to show, so visitors never see a dev-only empty state. -->
    <section class="bg-white py-24 sm:py-32 overflow-hidden">
      <div class="max-w-6xl mx-auto px-5 sm:px-8">
        <div class="text-center mb-12">
          <div class="inline-flex items-center gap-2 mb-3">
            <span class="adme-dot adme-dot-emerald"></span>
            <span class="adme-eyebrow">Live from the showroom floor</span>
          </div>
          <h2 class="adme-h2 text-4xl sm:text-5xl md:text-6xl mb-4">
            Capturing Every Handover Moment<br class="hidden sm:inline"/> Across Your Network
          </h2>
          <p class="adme-lede text-base">Every submission automatically blurs license plates and records customer consent.</p>
        </div>
      </div>

      <!-- Single Square-style mosaic row: tall portrait + a column of two
           stacked landscape tiles. -->
      <div v-if="allPhotos.length > 0" class="photo-marquee overflow-hidden" :class="{ 'is-resizing': isResizing }">
        <div class="photo-marquee-track photo-marquee-row-1 flex gap-4 w-max items-stretch h-[32rem] sm:h-[40rem]">
          <!-- All tiles load eagerly. Native loading="lazy" is unreliable
               inside the continuously-transformed marquee track: Safari/WebKit
               computes each image's intersection against its *animated*
               position (the track is masked + `contain: layout paint`), so it
               never loads — or unloads mid-scroll — off-screen tiles, leaving
               blank white boxes. The curated set is small (~10 photos × a
               handful of dealers), so eager loading is cheap and correct. -->
          <template v-for="(cluster, cIdx) in mosaicRowOne">
            <div :key="`m-p-${cIdx}`"
                 class="flex-shrink-0 h-full rounded-2xl overflow-hidden bg-white shadow-[0_4px_20px_rgba(0,0,0,0.06)] border border-[#e8e8ed]">
              <img :src="cluster[0]" loading="eager" decoding="async"
                   class="block w-[22rem] sm:w-[40rem] h-full object-cover"
                   alt="Customer delivery moment" />
            </div>
            <div :key="`m-s-${cIdx}`" class="flex-shrink-0 h-full flex flex-col gap-4 w-[22rem] sm:w-[28rem]">
              <div class="flex-1 min-h-0 rounded-2xl overflow-hidden bg-white shadow-[0_4px_20px_rgba(0,0,0,0.06)] border border-[#e8e8ed]">
                <img :src="cluster[1]" loading="eager" decoding="async"
                     class="block w-full h-full object-cover"
                     alt="Customer delivery moment" />
              </div>
              <div class="flex-1 min-h-0 rounded-2xl overflow-hidden bg-white shadow-[0_4px_20px_rgba(0,0,0,0.06)] border border-[#e8e8ed]">
                <img :src="cluster[2]" loading="eager" decoding="async"
                     class="block w-full h-full object-cover"
                     alt="Customer delivery moment" />
              </div>
            </div>
          </template>
        </div>
      </div>
      <div v-else-if="photosLoading" class="overflow-hidden">
        <div class="flex gap-4 px-5 sm:px-8 items-stretch h-[32rem] sm:h-[40rem]">
          <template v-for="n in 4">
            <div :key="`sk-p-${n}`" class="flex-shrink-0 h-full w-[22rem] sm:w-[40rem] rounded-2xl bg-[#ececef] border border-[#e8e8ed] animate-pulse"></div>
            <div :key="`sk-s-${n}`" class="flex-shrink-0 h-full flex flex-col gap-4 w-[22rem] sm:w-[28rem]">
              <div class="flex-1 min-h-0 rounded-2xl bg-[#ececef] border border-[#e8e8ed] animate-pulse"></div>
              <div class="flex-1 min-h-0 rounded-2xl bg-[#ececef] border border-[#e8e8ed] animate-pulse"></div>
            </div>
          </template>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════════ TRACKING / CHART (light) ═══════════════════════════════ -->
    <section id="tracking" class="bg-[#f5f5f7] py-24 sm:py-32">
      <div class="max-w-6xl mx-auto px-5 sm:px-8">
        <div class="text-center mb-14">
          <p class="adme-eyebrow mb-3">Live signal, not vanity metrics</p>
          <h2 class="adme-h2 text-4xl sm:text-5xl md:text-6xl mb-5">Track What Was Sent.<br class="hidden sm:inline"/> See Who Engaged.</h2>
          <p class="adme-lede text-lg max-w-2xl mx-auto">
            Every welcome email and SMS is tracked end-to-end — queued, sent, delivered, opened, clicked.
          </p>
        </div>

        <!-- Chart card -->
        <div class="adme-card p-6 sm:p-10">
          <div class="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
            <div>
              <div class="inline-flex items-center gap-2 mb-2">
                <span class="adme-dot adme-dot-emerald"></span>
                <span class="adme-eyebrow">Engagement</span>
              </div>
              <h3 class="text-xl sm:text-2xl font-semibold tracking-tight text-[#1d1d1f]">Weekly captures across the network</h3>
              <p class="text-sm text-[#6e6e73] mt-1">Live count of customer welcome submissions, all dealers, last 12 weeks.</p>
            </div>
            <div class="flex items-center gap-1.5">
              <span v-for="r in ['7d','30d','90d','12w']" :key="r"
                    class="adme-range-chip"
                    :class="r === '12w' ? 'is-active' : ''">{{ r }}</span>
            </div>
          </div>

          <div class="relative" style="aspect-ratio: 16 / 6;">
            <svg viewBox="0 0 800 300" class="w-full h-full" preserveAspectRatio="none">
              <defs>
                <linearGradient id="chartFillLight" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stop-color="#0071e3" stop-opacity="0.18" />
                  <stop offset="100%" stop-color="#0071e3" stop-opacity="0" />
                </linearGradient>
              </defs>
              <g>
                <line v-for="(y, i) in [40, 100, 160, 220, 280]" :key="`gx-${i}`"
                      x1="48" :y1="y" x2="788" :y2="y"
                      stroke="rgba(0,0,0,0.06)" stroke-dasharray="2 4" />
              </g>
              <g font-size="10" fill="#86868b" font-family="Inter, system-ui, sans-serif">
                <text v-for="(t, i) in yAxisTicks" :key="`yt-${i}`"
                      x="40" :y="40 + i * 60 + 3" text-anchor="end">{{ t }}</text>
              </g>
              <path :d="chartAreaPathPro" fill="url(#chartFillLight)" />
              <path :d="chartLinePathPro" fill="none" stroke="#0071e3" stroke-width="2.5"
                    stroke-linejoin="round" stroke-linecap="round" />
              <g v-for="(pt, i) in chartPointsPro" :key="`pt-${i}`">
                <circle :cx="pt.x" :cy="pt.y" r="3" fill="#ffffff" stroke="#0071e3" stroke-width="1.75" />
              </g>
              <g font-size="10" fill="#86868b" font-family="Inter, system-ui, sans-serif">
                <text v-for="(lbl, i) in chartLabels" :key="`xl-${i}`"
                      :x="chartPointsPro[i] ? chartPointsPro[i].x : 0" y="295" text-anchor="middle">{{ lbl }}</text>
              </g>
            </svg>
          </div>

          <div class="mt-6 pt-6 border-t border-[#e8e8ed] grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <p class="adme-eyebrow mb-1">Total captures</p>
              <p class="text-[#1d1d1f] text-lg font-semibold tracking-tight tabular-nums">{{ chartTotal.toLocaleString() }}</p>
            </div>
            <div>
              <p class="adme-eyebrow mb-1">Weekly average</p>
              <p class="text-[#1d1d1f] text-lg font-semibold tracking-tight tabular-nums">{{ chartAvg.toLocaleString() }}</p>
            </div>
            <div>
              <p class="adme-eyebrow mb-1">Best week</p>
              <p class="text-[#1d1d1f] text-lg font-semibold tracking-tight tabular-nums">{{ chartPeak.toLocaleString() }}</p>
            </div>
            <div>
              <p class="adme-eyebrow mb-1">Change WoW</p>
              <p class="text-emerald-600 text-lg font-semibold tracking-tight tabular-nums inline-flex items-center gap-1.5">
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" />
                </svg>
                {{ chartWoW }}%
              </p>
            </div>
          </div>
        </div>

        <!-- Apple-style mid-page CTA: punctuates the chart-card with a single direct action. -->
        <div class="mt-16 sm:mt-20 text-center">
          <h3 class="adme-h2 text-3xl sm:text-4xl md:text-5xl mb-5">See Your Dealership on This Chart</h3>
          <p class="adme-lede text-base sm:text-lg max-w-xl mx-auto mb-8">
            Live captures, opens, clicks, deliveries — every dealer in your group, in one view.
          </p>
          <div class="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href="#contact" class="adme-btn adme-btn-primary">
              Request a Demo
              <svg class="ml-2 w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
            <a href="#dashboard" class="adme-btn adme-btn-link text-[#0071e3] hover:text-[#0077ed]">
              See the Dashboard
              <svg class="ml-1 w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════════ WATCH IT WORK — VIDEO (dark) ═══════════════════════════════ -->
    <!-- Dark surface (same recipe as hero/footer) gives the player strong contrast
         and sets up the four-step "How It Works" flow that follows. -->
    <section id="watch" class="relative overflow-hidden py-24 sm:py-32" style="background: #0a0a0c;">
      <div class="absolute inset-0 z-0 pointer-events-none">
        <img src="/assets/photo.jpg" alt="" class="w-full h-full object-cover opacity-50 bg-image-animate" />
      </div>
      <div class="absolute inset-0 z-0 pointer-events-none bg-gradient-to-br from-blue-900/70 via-blue-900/70 to-gray-900/70 mix-blend-multiply"></div>
      <div class="absolute inset-0 z-0 pointer-events-none bg-black/40 backdrop-blur-[2px]"></div>

      <div class="relative z-10 max-w-5xl mx-auto px-5 sm:px-8">
        <div class="text-center mb-12 sm:mb-14">
          <p class="adme-eyebrow mb-3" style="color: rgba(159, 191, 255, 0.85);">See it end-to-end</p>
          <h2 class="adme-h2-dark text-4xl sm:text-5xl md:text-6xl mb-5">This Is How the Welcome Email Works.</h2>
          <p class="text-[#a1a1a6] text-lg leading-relaxed max-w-2xl mx-auto">
            From the delivery photo on the showroom floor to the dealer-branded eDM landing in your customer's inbox —
            watch the whole flow in under two minutes.
          </p>
        </div>

        <!-- Player card — YouTube facade. Until the visitor clicks, we paint a
             static poster (a hand-picked frame from the video) with a play button;
             only on click do we mount the iframe with autoplay. This means zero
             YouTube JS/cookies load on page view — the embed cost is paid only by
             people who actually watch. -->
        <div class="relative rounded-2xl sm:rounded-3xl overflow-hidden bg-black border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.45)]"
             style="aspect-ratio: 16 / 9;">
          <iframe
            v-if="videoPlaying"
            ref="videoIframe"
            class="block w-full h-full"
            src="https://www.youtube-nocookie.com/embed/-zabVSV5EWA?rel=0&autoplay=1&playsinline=1&enablejsapi=1"
            title="How the Welcome Email works"
            frameborder="0"
            referrerpolicy="strict-origin-when-cross-origin"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowfullscreen
            @load="startVideo"></iframe>
          <button
            v-else
            type="button"
            @click="videoPlaying = true"
            class="group absolute inset-0 w-full h-full cursor-pointer"
            aria-label="Play: How the Welcome Email works">
            <img src="/assets/edm/welcome-poster-video.png" alt=""
                 loading="lazy" decoding="async"
                 class="absolute inset-0 w-full h-full object-cover" />
            <span class="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors"></span>
            <!-- YouTube-style play button: red rounded-rect badge, white triangle.
                 Centred with the translate-(-50%,-50%) trick so it stays dead-centre
                 regardless of the card's aspect ratio. The triangle's geometry is
                 baked into the SVG path (no translate hacks needed). -->
            <span class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10
                         w-[68px] h-[48px] sm:w-[88px] sm:h-[62px]
                         transition-transform duration-200 ease-out group-hover:scale-110">
              <svg viewBox="0 0 68 48" class="w-full h-full drop-shadow-[0_4px_14px_rgba(0,0,0,0.4)]" aria-hidden="true">
                <path class="transition-colors duration-200 fill-[#212121]/80 group-hover:fill-[#ff0000]"
                      d="M66.52 7.74a8 8 0 0 0-5.63-5.66C55.79.5 34 .5 34 .5s-21.79 0-26.89 1.58a8 8 0 0 0-5.63 5.66A83.7 83.7 0 0 0 0 24a83.7 83.7 0 0 0 1.48 16.26 8 8 0 0 0 5.63 5.66C12.21 47.5 34 47.5 34 47.5s21.79 0 26.89-1.58a8 8 0 0 0 5.63-5.66A83.7 83.7 0 0 0 68 24a83.7 83.7 0 0 0-1.48-16.26Z" />
                <path d="M45 24 27 14v20l18-10Z" fill="#ffffff" />
              </svg>
            </span>
          </button>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════════ HOW IT WORKS (white) ═══════════════════════════════ -->
    <section id="how-it-works" class="bg-white py-24 sm:py-32">
      <div class="max-w-6xl mx-auto px-5 sm:px-8">
        <div class="text-center mb-14">
          <p class="adme-eyebrow mb-3">The capture flow</p>
          <h2 class="adme-h2 text-4xl sm:text-5xl md:text-6xl mb-5">Four Steps. Under a Minute Per Customer.</h2>
          <p class="adme-lede text-lg max-w-2xl mx-auto">
            From phone-camera to permanent record, in the same flow your staff already use at handover.
          </p>
        </div>

        <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          <div v-for="(step, i) in steps" :key="step.title" class="adme-card p-7">
            <div class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#f5f5f7] text-[#86868b] text-[11px] font-semibold mb-4">{{ i + 1 }}</div>
            <h3 class="text-lg font-semibold tracking-tight text-[#1d1d1f] mb-2">{{ step.title }}</h3>
            <p class="text-[15px] text-[#6e6e73] leading-relaxed">{{ step.body }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════════ DARK ACCENT — Email + SMS ═══════════════════════════════ -->
    <section class="dark-accent relative overflow-hidden py-24 sm:py-32" style="background: #0a0a0c;">
      <!-- Same background recipe as the hero + footer -->
      <div class="absolute inset-0 z-0 pointer-events-none">
        <img src="/assets/photo.jpg" alt="" class="w-full h-full object-cover opacity-50 bg-image-animate" />
      </div>
      <div class="absolute inset-0 z-0 pointer-events-none bg-gradient-to-br from-blue-900/70 via-blue-900/70 to-gray-900/70 mix-blend-multiply"></div>
      <div class="absolute inset-0 z-0 pointer-events-none bg-black/40 backdrop-blur-[2px]"></div>

      <div class="relative z-10 max-w-6xl mx-auto px-5 sm:px-8">
        <div class="text-center mb-14">
          <p class="adme-eyebrow mb-3" style="color: rgba(159, 191, 255, 0.85);">Multi-channel, multi-status</p>
          <h2 class="adme-h2-dark text-4xl sm:text-5xl md:text-6xl mb-5">Every Email. Every SMS. Every Status.</h2>
          <p class="text-[#a1a1a6] text-lg leading-relaxed max-w-2xl mx-auto">
            Every message is tracked from the moment it leaves the showroom to the moment your customer opens it —
            aggregated per submission and shown back in one timeline.
          </p>
        </div>

        <!-- Engagr in-flight orbital — same motif as the form's submit loader.
             Five dots orbit the Engagr wordmark to suggest messages in flight. -->
        <div class="engagr-orbital mx-auto mb-16 sm:mb-20" aria-hidden="true">
          <img src="/assets/in-flight.svg" alt="" class="engagr-orbital-logo" />
          <div class="engagr-orbital-dots">
            <span class="engagr-orbital-dot"></span>
            <span class="engagr-orbital-dot"></span>
            <span class="engagr-orbital-dot"></span>
            <span class="engagr-orbital-dot"></span>
            <span class="engagr-orbital-dot"></span>
          </div>
        </div>

        <div class="grid md:grid-cols-2 gap-5">
          <!-- Email -->
          <div class="adme-card-dark p-7 sm:p-8">
            <div class="flex items-center gap-3 mb-6">
              <div class="adme-icon-tile-dark">
                <svg class="w-4 h-4 text-[#9fbfff]" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <p class="adme-eyebrow" style="color: rgba(159, 191, 255, 0.85);">Email</p>
                <h3 class="text-lg font-semibold tracking-tight text-white">Welcome Email Tracking</h3>
              </div>
            </div>
            <div class="space-y-2">
              <div v-for="b in emailBadges" :key="b.label" class="adme-status-row-dark">
                <span class="inline-flex items-center gap-2.5 text-sm text-white">
                  <span :class="['w-2 h-2 rounded-full', b.dot]"></span>{{ b.label }}
                </span>
                <span class="text-xs text-[#86868b]">{{ b.desc }}</span>
              </div>
            </div>
            <p class="text-xs text-[#86868b] mt-5 leading-relaxed">
              Bounced emails surface a one-click resend; opens and clicks roll up into per-account engagement charts.
            </p>
          </div>

          <!-- SMS -->
          <div class="adme-card-dark p-7 sm:p-8">
            <div class="flex items-center gap-3 mb-6">
              <div class="adme-icon-tile-dark">
                <svg class="w-4 h-4 text-[#9fbfff]" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div>
                <p class="adme-eyebrow" style="color: rgba(159, 191, 255, 0.85);">SMS</p>
                <h3 class="text-lg font-semibold tracking-tight text-white">SMS Delivery Tracking</h3>
              </div>
            </div>
            <div class="space-y-2">
              <div v-for="b in smsBadges" :key="b.label" class="adme-status-row-dark">
                <span class="inline-flex items-center gap-2.5 text-sm text-white">
                  <span :class="['w-2 h-2 rounded-full', b.dot]"></span>{{ b.label }}
                </span>
                <span class="text-xs text-[#86868b]">{{ b.desc }}</span>
              </div>
            </div>
            <p class="text-xs text-[#86868b] mt-5 leading-relaxed">
              Live status as the carrier accepts, delivers or rejects each message — with the error code, message ID and per-segment cost surfaced on every failure.
            </p>
          </div>
        </div>

        <!-- Apple-style dark-section CTA: one bold action, lots of air. -->
        <div class="mt-20 sm:mt-24 text-center">
          <h3 class="adme-h2-dark text-3xl sm:text-4xl md:text-5xl mb-5">Send Everything. Track Everything.</h3>
          <p class="text-[#a1a1a6] text-base sm:text-lg leading-relaxed max-w-xl mx-auto mb-8">
            One welcome capture. Email, SMS, social and review request — every channel, every status, in one place.
          </p>
          <a href="#contact" class="adme-btn adme-btn-primary">
            Request a Demo
            <svg class="ml-2 w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </a>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════════ DASHBOARD / LEADERBOARD + TIMELINE (light) ═══════════════════════════════ -->
    <section id="dashboard" class="bg-[#f5f5f7] py-24 sm:py-32">
      <div class="max-w-6xl mx-auto px-5 sm:px-8">
        <div class="text-center mb-14">
          <p class="adme-eyebrow mb-3">Dashboard surfaces</p>
          <h2 class="adme-h2 text-4xl sm:text-5xl md:text-6xl mb-5">Per-Dealer Staff. Per-Agency Principal.</h2>
          <p class="adme-lede text-lg max-w-2xl mx-auto">
            A leaderboard the floor manager can read at a glance.
            A delivery timeline the agency principal trusts as the source of truth.
          </p>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <!-- Leaderboard -->
          <div class="adme-card p-7 sm:p-8">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
              <div>
                <p class="adme-eyebrow">Team performance</p>
                <h3 class="text-2xl font-semibold tracking-tight text-[#1d1d1f]">Top salespersons</h3>
                <p class="text-sm text-[#86868b] mt-1">Across the network · last 30 days</p>
              </div>
              <span class="adme-range-chip self-start sm:self-auto">
                <span class="adme-dot adme-dot-emerald mr-1.5"></span>
                Live
              </span>
            </div>
            <div class="space-y-2.5">
              <div v-for="(entry, index) in topSalespersons" :key="entry.name"
                   class="flex items-center justify-between p-4 bg-[#fafafa] rounded-2xl transition-all duration-300 ease-out hover:bg-[#f0f0f3] group">
                <div class="flex-shrink-0">
                  <div :class="rankBadgeClasses(index)">{{ index + 1 }}</div>
                </div>
                <div class="flex-1 min-w-0 ml-4">
                  <h4 class="text-[15px] font-medium text-[#1d1d1f] truncate">{{ entry.name }}</h4>
                  <p class="text-xs text-[#86868b] mt-0.5 truncate">{{ entry.dealer }} · {{ entry.count }} captures</p>
                </div>
                <div class="flex items-center gap-3 flex-shrink-0 ml-2">
                  <div class="hidden sm:block w-24 h-1.5 rounded-full bg-white overflow-hidden border border-[#e8e8ed]">
                    <div class="h-full bg-[#0071e3]" :style="{ width: `${Math.round((entry.count / topSalespersons[0].count) * 100)}%` }"></div>
                  </div>
                  <span class="text-sm font-medium text-[#1d1d1f] tabular-nums whitespace-nowrap">{{ Math.round((entry.count / topSalespersons[0].count) * 100) }}%</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Delivery Timeline -->
          <div class="adme-card p-7 sm:p-8">
            <div class="mb-6">
              <p class="adme-eyebrow">Delivery timeline</p>
              <h3 class="text-2xl font-semibold tracking-tight text-[#1d1d1f]">What happens to every capture</h3>
              <p class="text-sm text-[#86868b] mt-1">Same timeline UI as the in-app SMS detail view</p>
            </div>
            <div class="space-y-3">
              <div v-for="(evt, index) in deliveryTimeline" :key="evt.type" class="flex items-start gap-4 relative">
                <div v-if="index < deliveryTimeline.length - 1"
                     class="absolute left-3 top-7 w-px h-full bg-gradient-to-b from-[#d2d2d7] to-transparent"></div>
                <div :class="['relative z-10 flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center', timelineDotClasses(evt.tone)]">
                  <div class="w-1.5 h-1.5 rounded-full bg-current"></div>
                </div>
                <div class="flex-1 bg-[#fafafa] rounded-2xl p-3.5">
                  <div class="flex items-center justify-between gap-2">
                    <span class="text-[15px] font-medium text-[#1d1d1f]">{{ evt.type }}</span>
                    <span class="text-[10px] font-semibold tracking-wider text-[#86868b] uppercase">Step {{ index + 1 }}</span>
                  </div>
                  <div class="text-[13px] text-[#6e6e73] mt-1 leading-relaxed">{{ evt.desc }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════════ AUDIT TRAIL (white) — SMS + Welcome Email Delivery Details ═══════════════════════════════ -->
    <section class="bg-white py-24 sm:py-32">
      <div class="max-w-6xl mx-auto px-5 sm:px-8">
        <div class="text-center mb-14">
          <p class="adme-eyebrow mb-3">Audit trail</p>
          <h2 class="adme-h2 text-4xl sm:text-5xl md:text-6xl mb-5">The Receipt Your Principal Trusts.</h2>
          <p class="adme-lede text-lg max-w-2xl mx-auto">
            Open any capture and see the exact card sent, the exact channel it went out on, and the exact moment each status changed.
          </p>
        </div>

        <!-- SMS Delivery Details — mirrors the in-app modal -->
        <div class="adme-card p-7 sm:p-8">
          <div class="mb-6 pb-5 border-b border-[#e8e8ed]">
            <h3 class="text-2xl font-semibold tracking-tight text-[#1d1d1f]">SMS Delivery Details</h3>
            <p class="text-sm text-[#86868b] mt-0.5">The exact card the agency principal opens to audit a send</p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            <!-- Phone-mockup SMS preview -->
            <div>
              <p class="adme-eyebrow mb-3">SMS Preview</p>
              <div class="rounded-[28px] overflow-hidden bg-[#0a0a0c] shadow-[0_10px_30px_rgba(0,0,0,0.08)] border border-[#1d1d1f]">
                <div class="flex items-center justify-between px-5 pt-4 pb-3 text-white text-[12px] font-medium">
                  <span>••• engagr</span>
                  <span>11:11 AM &nbsp;100%</span>
                </div>
                <div class="px-5 pb-3 flex items-center justify-between">
                  <div>
                    <div class="text-white text-[15px] font-semibold leading-tight">McRae Motors</div>
                    <div class="text-white/50 text-[12px] mt-0.5">Active now</div>
                  </div>
                  <svg class="w-5 h-5 text-[#0071e3]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20 15.5c-1.25 0-2.45-.2-3.57-.57a1 1 0 00-1.02.24l-2.2 2.2a15.05 15.05 0 01-6.59-6.59l2.2-2.21a1 1 0 00.25-1.01A11.36 11.36 0 018.5 4a1 1 0 00-1-1H4a1 1 0 00-1 1c0 9.39 7.61 17 17 17a1 1 0 001-1v-3.5a1 1 0 00-1-1z"/>
                  </svg>
                </div>
                <div class="px-5 pt-3 pb-6 bg-black min-h-[18rem]">
                  <div class="ml-auto max-w-[85%] bg-[#3aa14a] text-white rounded-[18px] rounded-br-[4px] px-3.5 py-2.5 text-[13px] leading-relaxed">
                    Hi Kath, congratulations on the purchase of your new vehicle from McRae Motors!<br/>
                    We'd love for you to leave us a review — tap below to share your experience.
                  </div>
                  <div class="text-center text-white/50 text-[11px] mt-2">11:11 AM · Dealer SMS number</div>
                  <div class="flex justify-center mt-3">
                    <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-300 text-[11px] font-medium">
                      <span class="adme-dot adme-dot-emerald"></span>
                      Delivered
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Numbers + delivery timeline -->
            <div>
              <div class="grid grid-cols-2 gap-3 mb-6">
                <div class="bg-[#f5f5f7] rounded-2xl p-4">
                  <div class="text-[11px] font-semibold tracking-wider text-[#86868b] uppercase mb-1">From Number</div>
                  <div class="text-[15px] font-medium text-[#1d1d1f]">Dealer SMS sender</div>
                </div>
                <div class="bg-[#f5f5f7] rounded-2xl p-4">
                  <div class="text-[11px] font-semibold tracking-wider text-[#86868b] uppercase mb-1">To Number</div>
                  <div class="text-[15px] font-medium text-[#1d1d1f]">Customer mobile</div>
                </div>
              </div>

              <p class="adme-eyebrow mb-3">Delivery Timeline</p>
              <div class="space-y-3">
                <div class="flex items-start gap-4 relative">
                  <div class="absolute left-3 top-7 w-px h-full bg-gradient-to-b from-[#d2d2d7] to-transparent"></div>
                  <div class="relative z-10 flex-shrink-0 w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center">
                    <div class="w-1.5 h-1.5 rounded-full bg-[#0071e3]"></div>
                  </div>
                  <div class="flex-1 bg-[#f5f5f7] rounded-2xl p-3.5">
                    <div class="flex items-center justify-between gap-2">
                      <span class="text-[15px] font-medium text-[#1d1d1f]">Sent</span>
                      <span class="text-[12px] text-[#86868b] tabular-nums">May 18, 2026 at 11:11:34 AM</span>
                    </div>
                    <div class="text-[12px] text-[#86868b] mt-0.5">1 day ago</div>
                  </div>
                </div>
                <div class="flex items-start gap-4 relative">
                  <div class="relative z-10 flex-shrink-0 w-6 h-6 rounded-full bg-emerald-50 flex items-center justify-center">
                    <div class="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                  </div>
                  <div class="flex-1 bg-[#f5f5f7] rounded-2xl p-3.5">
                    <div class="flex items-center justify-between gap-2">
                      <span class="text-[15px] font-medium text-[#1d1d1f]">Delivered</span>
                      <span class="text-[12px] text-[#86868b] tabular-nums">May 18, 2026 at 11:11:39 AM</span>
                    </div>
                    <div class="text-[12px] text-[#86868b] mt-0.5">1 day ago</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Email Delivery Details — mirrors the in-app email modal -->
        <div class="adme-card p-7 sm:p-8 mt-5">
          <div class="mb-6 pb-5 border-b border-[#e8e8ed]">
            <h3 class="text-2xl font-semibold tracking-tight text-[#1d1d1f]">Welcome Email Delivery Details</h3>
            <p class="text-sm text-[#86868b] mt-0.5">The dealer-branded eDM your customer receives — composed per-dealer, sent on your behalf, and tracked end-to-end.</p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            <!-- Desktop-style email preview -->
            <div>
              <p class="adme-eyebrow mb-3">Email Preview</p>
              <div class="rounded-2xl overflow-hidden bg-white shadow-[0_10px_30px_rgba(0,0,0,0.08)] border border-[#e8e8ed]">
                <!-- Client chrome -->
                <div class="flex items-center gap-1.5 px-4 py-2.5 bg-[#f5f5f7] border-b border-[#e8e8ed]">
                  <span class="w-2.5 h-2.5 rounded-full bg-[#ff5f57]"></span>
                  <span class="w-2.5 h-2.5 rounded-full bg-[#febc2e]"></span>
                  <span class="w-2.5 h-2.5 rounded-full bg-[#28c840]"></span>
                  <span class="ml-3 text-[11px] text-[#86868b]">Inbox</span>
                </div>
                <!-- Subject + meta -->
                <div class="px-5 pt-4 pb-3 border-b border-[#e8e8ed]">
                  <div class="text-[15px] font-semibold text-[#1d1d1f] leading-tight">Welcome to McRae Motors, Kath</div>
                  <div class="flex items-center gap-2 mt-2 text-[12px] text-[#86868b] flex-wrap">
                    <span class="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#0071e3] text-white text-[10px] font-semibold flex-shrink-0">M</span>
                    <span class="text-[#1d1d1f] font-medium">McRae Motors</span>
                    <span class="italic">from your dealer's sender address</span>
                    <span class="ml-auto">11:11 AM</span>
                  </div>
                </div>
                <!-- eDM hero (dealer-branded header) -->
                <div class="relative bg-gradient-to-br from-blue-900 via-blue-900 to-gray-900 px-5 py-6 text-center">
                  <div class="text-[10px] font-semibold tracking-widest text-blue-200 uppercase mb-1">McRae Motors</div>
                  <div class="text-white text-[18px] font-semibold tracking-tight leading-tight">Welcome to the family, Kath.</div>
                </div>
                <!-- eDM body -->
                <div class="px-5 py-5">
                  <div class="text-[13px] text-[#1d1d1f] leading-relaxed">
                    Hi Kath,<br/><br/>
                    A huge congratulations on the purchase of your new vehicle from all of us at McRae Motors. We've included your delivery photo as a memento of the day.
                  </div>
                  <!-- Locked: Kath's delivery photo from McRae Motors. Hand-picked by Robert — do not swap. -->
                  <div class="mt-4 rounded-xl overflow-hidden bg-[#f5f5f7] border border-[#e8e8ed]">
                    <img src="https://engagrcdn.b-cdn.net/content/uploads/gravity_forms/welcome_1779432689591_xaxin.jpg"
                         alt="Kath's delivery photo from McRae Motors"
                         loading="lazy"
                         class="block w-full h-auto" />
                  </div>

                  <!-- Service & owner essentials -->
                  <div class="mt-5 pt-5 border-t border-[#e8e8ed]">
                    <div class="text-[11px] font-semibold tracking-widest text-[#86868b] uppercase mb-2">Owner essentials</div>
                    <ul class="text-[12px] text-[#1d1d1f] leading-relaxed space-y-1.5">
                      <li>
                        <span class="text-[#86868b]">First service due:&nbsp;</span>
                        <span class="font-medium">10,000 km or 12 months</span>
                      </li>
                      <li>
                        <span class="text-[#86868b]">Capped-price servicing:&nbsp;</span>
                        <span class="font-medium">Included for the first 5 years</span>
                      </li>
                      <li>
                        <span class="text-[#86868b]">Warranty:&nbsp;</span>
                        <span class="font-medium">5 years / unlimited km</span>
                      </li>
                      <li>
                        <span class="text-[#86868b]">Roadside assistance:&nbsp;</span>
                        <span class="font-medium">24/7 roadside hotline</span>
                      </li>
                    </ul>
                    <a class="mt-3 inline-flex items-center justify-center w-full px-4 py-2.5 rounded-full bg-[#0071e3] text-white text-[13px] font-medium">
                      Book your first service
                    </a>
                  </div>

                  <!-- Dealership details -->
                  <div class="mt-5 pt-5 border-t border-[#e8e8ed]">
                    <div class="text-[11px] font-semibold tracking-widest text-[#86868b] uppercase mb-2">Your dealership</div>
                    <div class="text-[12px] text-[#1d1d1f] leading-relaxed">
                      <div class="font-medium">McRae Motors</div>
                      <div class="text-[#6e6e73]">Dealership address</div>
                      <div class="text-[#6e6e73] mt-1">Sales and service phone numbers</div>
                      <div class="text-[#6e6e73]">Opening hours</div>
                    </div>
                  </div>

                  <!-- Secondary CTAs -->
                  <div class="mt-4 flex flex-col gap-2">
                    <a class="inline-flex items-center justify-center w-full px-4 py-2.5 rounded-full border border-[#e8e8ed] text-[#1d1d1f] text-[13px] font-medium">
                      Leave us a Google review
                    </a>
                    <a class="inline-flex items-center justify-center w-full px-4 py-2.5 rounded-full border border-[#e8e8ed] text-[#1d1d1f] text-[13px] font-medium">
                      Share to Facebook
                    </a>
                  </div>
                  <div class="text-[11px] text-[#86868b] mt-4 leading-relaxed">
                    McRae Motors<br/>
                    You're receiving this because you bought a vehicle from us. <span class="underline">Unsubscribe</span>.
                  </div>
                </div>
              </div>
            </div>

            <!-- About this template + From/To + delivery timeline -->
            <div>
              <div class="mb-7 pb-6 border-b border-[#e8e8ed]">
                <p class="adme-eyebrow mb-3">About this template</p>
                <p class="text-sm text-[#6e6e73] leading-relaxed">
                  Every dealer gets their own template — subject line, body copy, logo and sender address are configured per account. Service schedule, warranty terms, contact numbers, opening hours and CTAs (book a service, leave a review, share to socials) are all editable per dealer.
                </p>
                <p class="text-sm text-[#6e6e73] leading-relaxed mt-3">
                  Every status streams into the dashboard the moment it happens — bounces and spam reports surface here too, with a one-click resend.
                </p>
              </div>

              <div class="grid grid-cols-2 gap-3 mb-6">
                <div class="bg-[#f5f5f7] rounded-2xl p-4">
                  <div class="text-[11px] font-semibold tracking-wider text-[#86868b] uppercase mb-1">From</div>
                  <div class="text-[15px] font-medium text-[#1d1d1f] break-all">Dealer notification sender</div>
                </div>
                <div class="bg-[#f5f5f7] rounded-2xl p-4">
                  <div class="text-[11px] font-semibold tracking-wider text-[#86868b] uppercase mb-1">To</div>
                  <div class="text-[15px] font-medium text-[#1d1d1f]">Customer email</div>
                </div>
              </div>

              <p class="adme-eyebrow mb-3">Delivery Timeline</p>
              <div class="space-y-3">
                <div class="flex items-start gap-4 relative">
                  <div class="absolute left-3 top-7 w-px h-full bg-gradient-to-b from-[#d2d2d7] to-transparent"></div>
                  <div class="relative z-10 flex-shrink-0 w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center">
                    <div class="w-1.5 h-1.5 rounded-full bg-[#0071e3]"></div>
                  </div>
                  <div class="flex-1 bg-[#f5f5f7] rounded-2xl p-3.5">
                    <div class="flex items-center justify-between gap-2">
                      <span class="text-[15px] font-medium text-[#1d1d1f]">Sent</span>
                      <span class="text-[12px] text-[#86868b] tabular-nums">May 18, 2026 at 11:11:34 AM</span>
                    </div>
                    <div class="text-[12px] text-[#86868b] mt-0.5">Accepted for delivery</div>
                  </div>
                </div>
                <div class="flex items-start gap-4 relative">
                  <div class="absolute left-3 top-7 w-px h-full bg-gradient-to-b from-[#d2d2d7] to-transparent"></div>
                  <div class="relative z-10 flex-shrink-0 w-6 h-6 rounded-full bg-emerald-50 flex items-center justify-center">
                    <div class="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                  </div>
                  <div class="flex-1 bg-[#f5f5f7] rounded-2xl p-3.5">
                    <div class="flex items-center justify-between gap-2">
                      <span class="text-[15px] font-medium text-[#1d1d1f]">Delivered</span>
                      <span class="text-[12px] text-[#86868b] tabular-nums">May 18, 2026 at 11:11:42 AM</span>
                    </div>
                    <div class="text-[12px] text-[#86868b] mt-0.5">Receiving mailbox accepted</div>
                  </div>
                </div>
                <div class="flex items-start gap-4 relative">
                  <div class="absolute left-3 top-7 w-px h-full bg-gradient-to-b from-[#d2d2d7] to-transparent"></div>
                  <div class="relative z-10 flex-shrink-0 w-6 h-6 rounded-full bg-emerald-50 flex items-center justify-center">
                    <div class="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                  </div>
                  <div class="flex-1 bg-[#f5f5f7] rounded-2xl p-3.5">
                    <div class="flex items-center justify-between gap-2">
                      <span class="text-[15px] font-medium text-[#1d1d1f]">Opened</span>
                      <span class="text-[12px] text-[#86868b] tabular-nums">May 18, 2026 at 12:04:18 PM</span>
                    </div>
                    <div class="text-[12px] text-[#86868b] mt-0.5">Open count: 1</div>
                  </div>
                </div>
                <div class="flex items-start gap-4 relative">
                  <div class="relative z-10 flex-shrink-0 w-6 h-6 rounded-full bg-emerald-50 flex items-center justify-center">
                    <div class="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                  </div>
                  <div class="flex-1 bg-[#f5f5f7] rounded-2xl p-3.5">
                    <div class="flex items-center justify-between gap-2">
                      <span class="text-[15px] font-medium text-[#1d1d1f]">Clicked</span>
                      <span class="text-[12px] text-[#86868b] tabular-nums">May 18, 2026 at 12:05:02 PM</span>
                    </div>
                    <div class="text-[12px] text-[#86868b] mt-0.5">Click count: 1 · Review CTA</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Apple-style dashboard-section CTA: closes the proof stack with action. -->
        <div class="mt-20 sm:mt-24 text-center">
          <h3 class="adme-h2 text-3xl sm:text-4xl md:text-5xl mb-5">Ready to See It on Your Dealership's Data?</h3>
          <p class="adme-lede text-base sm:text-lg max-w-xl mx-auto mb-8">
            A 20-minute walkthrough with your real account names, brands and dealer logos in place.
          </p>
          <div class="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href="#contact" class="adme-btn adme-btn-primary">
              Request a Demo
              <svg class="ml-2 w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
            <a href="#how-it-works" class="adme-btn adme-btn-link text-[#0071e3] hover:text-[#0077ed]">
              How It Works
              <svg class="ml-1 w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════════ WHY ENGAGR (light grey) ═══════════════════════════════ -->
    <section class="bg-[#f5f5f7] py-24 sm:py-32">
      <div class="max-w-6xl mx-auto px-5 sm:px-8">
        <div class="text-center mb-14">
          <p class="adme-eyebrow mb-3">Built-in capability</p>
          <h2 class="adme-h2 text-4xl sm:text-5xl md:text-6xl mb-5">A Real Product, Not a Content Brief.</h2>
        </div>

        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          <div v-for="reason in reasons" :key="reason.title" class="adme-card p-7">
            <h3 class="text-lg font-semibold tracking-tight text-[#1d1d1f] mb-2">{{ reason.title }}</h3>
            <p class="text-[15px] text-[#6e6e73] leading-relaxed">{{ reason.body }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════════ CONTACT (off-white) ═══════════════════════════════ -->
    <section id="contact" class="bg-white py-24 sm:py-32">
      <div class="max-w-2xl mx-auto px-5 sm:px-8">
        <div class="adme-card p-7 sm:p-10">
          <div v-if="sent" class="text-center py-4">
            <div class="mx-auto w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mb-5">
              <svg class="w-7 h-7 text-emerald-600" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 class="text-xl font-semibold tracking-tight text-[#1d1d1f] mb-2">Thanks — we'll be in touch shortly.</h3>
            <p class="text-[15px] text-[#6e6e73]">A member of the ADME team will reach out within one business day.</p>
          </div>

          <form v-else @submit.prevent="submit" class="space-y-4">
            <div class="text-center mb-7">
              <p class="adme-eyebrow mb-3">Request a demo</p>
              <h2 class="adme-h2 text-3xl sm:text-4xl mb-3">Get Welcome Email Running Today</h2>
              <p class="adme-lede text-base">Set-up takes less than a day. We'll walk you through the dashboard, integrations and rollout plan for your group.</p>
            </div>

            <div class="hp-field" aria-hidden="true">
              <label>Website</label>
              <input v-model="form.website" type="text" tabindex="-1" autocomplete="off" />
            </div>

            <div class="grid sm:grid-cols-2 gap-3">
              <div>
                <label class="adme-label">Your name</label>
                <input v-model="form.name" type="text" required placeholder="Jane Smith" class="adme-input" />
              </div>
              <div>
                <label class="adme-label">Dealership</label>
                <input v-model="form.dealership" type="text" placeholder="ABC Motors" class="adme-input" />
              </div>
            </div>

            <div class="grid sm:grid-cols-2 gap-3">
              <div>
                <label class="adme-label">Email</label>
                <input v-model="form.email" type="email" required placeholder="jane@dealership.com.au" class="adme-input" />
              </div>
              <div>
                <label class="adme-label">Phone <span class="text-[#86868b] font-normal">(optional)</span></label>
                <input v-model="form.phone" type="tel" placeholder="04XX XXX XXX" class="adme-input" />
              </div>
            </div>

            <div>
              <label class="adme-label">How can we help? <span class="text-[#86868b] font-normal">(optional)</span></label>
              <textarea v-model="form.message" rows="3"
                placeholder="Tell us about your dealership and what you'd like to achieve..."
                class="adme-input"></textarea>
            </div>

            <div v-if="error" class="text-red-700 text-sm bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">{{ error }}</div>

            <button type="submit" :disabled="submitting"
              class="w-full adme-btn adme-btn-primary text-[15px]"
              :class="submitting ? 'opacity-60 cursor-not-allowed' : ''">
              <svg v-if="submitting" class="flex-shrink-0 h-4 w-4 text-white animate-spin mr-2" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/>
                <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/>
              </svg>
              <span>{{ submitting ? 'Sending...' : 'Request a Demo' }}</span>
              <svg v-if="!submitting" class="flex-shrink-0 h-4 w-4 ml-2" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>

          </form>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════════ FOOTER (dark, with mega-wordmark) ═══════════════════════════════ -->
    <footer class="adme-footer relative overflow-hidden">
      <!-- Background photo + overlays — same recipe as /dashboard -->
      <div class="absolute inset-0 z-0 pointer-events-none">
        <img src="/assets/photo.jpg" alt="" class="w-full h-full object-cover opacity-50 bg-image-animate" />
      </div>
      <div class="absolute inset-0 z-0 pointer-events-none bg-gradient-to-br from-blue-900/70 via-blue-900/70 to-gray-900/70 mix-blend-multiply"></div>
      <div class="absolute inset-0 z-0 pointer-events-none bg-black/40 backdrop-blur-[2px]"></div>

      <div class="relative z-20 max-w-6xl mx-auto px-5 sm:px-8 pt-16 pb-10">
        <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 pb-10 border-b border-white/10">
          <div class="text-[13px] text-white/70">
            &copy; {{ currentYear }} Engagr · A product of
            <a href="https://adme.net.au" target="_blank" rel="noopener noreferrer" class="text-white hover:underline">ADME</a>
          </div>
          <nav class="flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px]">
            <a href="#watch" class="text-white/70 hover:text-white transition-colors">Watch</a>
            <a href="#how-it-works" class="text-white/70 hover:text-white transition-colors">How It Works</a>
            <a href="#tracking" class="text-white/70 hover:text-white transition-colors">Tracking</a>
            <a href="#dashboard" class="text-white/70 hover:text-white transition-colors">Dashboard</a>
            <a href="#contact" class="text-white/70 hover:text-white transition-colors">New enquiries</a>
            <a href="mailto:support@adme.net.au" class="text-white/70 hover:text-white transition-colors">Support</a>
            <span class="hidden sm:inline-block w-px h-4 bg-white/15"></span>
            <a href="https://www.facebook.com/ADMEAdvertising" target="_blank" rel="noopener noreferrer"
               class="text-white/70 hover:text-white transition-colors inline-flex items-center" aria-label="Facebook">
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.261c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/>
              </svg>
            </a>
            <a href="https://www.linkedin.com/company/adme-advertising" target="_blank" rel="noopener noreferrer"
               class="text-white/70 hover:text-white transition-colors inline-flex items-center" aria-label="LinkedIn">
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M19 0h-14C2.239 0 0 2.239 0 5v14c0 2.761 2.239 5 5 5h14c2.761 0 5-2.239 5-5V5c0-2.761-2.239-5-5-5zM8 19H5V8h3v11zM6.5 6.732C5.534 6.732 4.75 5.942 4.75 4.968 4.75 3.994 5.534 3.204 6.5 3.204c.966 0 1.75.79 1.75 1.764 0 .974-.784 1.764-1.75 1.764zM20 19h-3v-5.604c0-3.368-4-3.113-4 0V19h-3V8h3v1.765c1.396-2.586 7-2.777 7 2.476V19z"/>
              </svg>
            </a>
            <a href="https://www.youtube.com/@ADMEAdvertising" target="_blank" rel="noopener noreferrer"
               class="text-white/70 hover:text-white transition-colors inline-flex items-center" aria-label="YouTube">
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.546 15.568V8.432L15.818 12l-6.272 3.568z"/>
              </svg>
            </a>
          </nav>
        </div>
      </div>

      <!-- Mega wordmark — fills the page width, clips at the bottom.
           Each letter gets its own hover-zoom. -->
      <a href="#top" class="adme-mega-wordmark relative z-10" aria-label="Back to top">
        <span class="adme-mega-letter" aria-hidden="true">e</span>
        <span class="adme-mega-letter" aria-hidden="true">n</span>
        <span class="adme-mega-letter" aria-hidden="true">g</span>
        <span class="adme-mega-letter" aria-hidden="true">a</span>
        <span class="adme-mega-letter" aria-hidden="true">g</span>
        <span class="adme-mega-letter" aria-hidden="true">r</span>
        <span class="sr-only">engagr</span>
      </a>
    </footer>

    <!-- Apple-style sticky CTA pill — appears once past the hero, hides over the contact form. -->
    <transition name="sticky-cta">
      <a v-if="showStickyCta" href="#contact" class="adme-sticky-cta">
        Request a Demo
        <svg class="ml-1.5 w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
        </svg>
      </a>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';

const ICON = {
  camera:   'M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316zM16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z',
  document: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  share:    'M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z',
  shield:   'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z',
  bolt:     'M13 10V3L4 14h7v7l9-11h-7z',
  lock:     'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
  chart:    'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  users:    'M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a3 3 0 015.356-1.857M17 8a3 3 0 11-6 0 3 3 0 016 0zm-9 4a3 3 0 11-6 0 3 3 0 016 0z',
  inbox:    'M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4',
  chat:     'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
  cloud:    'M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z',
  layers:   'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10',
  gauge:    'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2',
};

export default {
  name: 'welcome-page',
  data() {
    return {
      logoURLBlack: '/assets/engagr-blk.svg',
      currentYear: new Date().getFullYear(),

      showStickyCta: false,
      isResizing: false,
      videoPlaying: false,

      kpis: [
        { label: 'Captures · month', value: '4,812', delta: '+18%',  icon: ICON.camera, spark: [12,18,15,22,28,24,33,31,38,42,45,52] },
        { label: 'Email open rate',  value: '68%',   delta: '+9 pts', icon: ICON.inbox,  spark: [55,58,56,60,62,61,64,63,65,66,67,68] },
        { label: 'SMS delivery',     value: '99.1%', delta: '+0.4 pts', icon: ICON.chat, spark: [97,97.5,98,98.2,98.6,98.4,98.8,99,99.2,99,99.1,99.1] },
        { label: 'Customer consent', value: '93%',   delta: 'stable',  icon: ICON.shield, spark: [88,90,89,91,92,91,93,92,94,93,93,93] },
      ],

      chartSeries: [120,142,165,158,190,215,232,248,275,298,340,382],
      chartLabels: ['W1','W2','W3','W4','W5','W6','W7','W8','W9','W10','W11','W12'],

      samplePhotos: [],
      // Curated showcase — only these accounts contribute photos to the
      // public marketing marquee. Hand-picked by ADME so we always know what
      // is on screen; no automatic discovery, no surprise QA uploads. Update
      // this list to feature different dealers.
      showcaseSlugs: [
        'astoria-gwm',                // https://engagr-adme.netlify.app/account/astoria-gwm/dashboard
        'gws-kia',                    // https://engagr-adme.netlify.app/account/gws-kia/dashboard
        'garry-warren-smith-honda',   // https://engagr-adme.netlify.app/account/garry-warren-smith-honda/dashboard
        'mcrae-motors',               // https://engagr-adme.netlify.app/account/mcrae-motors/dashboard
        'waverley-deepal',            // https://engagr-adme.netlify.app/account/waverley-deepal/dashboard
        'frankston-ford',             // https://engagr-adme.netlify.app/account/frankston-ford/dashboard
      ],
      activeSlugs: [],
      allPhotos: [],
      // Per-source photo buckets (keyed by dealer slug, plus 'samples' for the
      // local manifest). allPhotos is rebuilt by round-robin interleaving these
      // so adjacent marquee tiles are never the same brand. See _addPhotos().
      photoSources: {},
      photosLoading: true,

      emailBadges: [
        { label: 'Delivered', dot: 'bg-emerald-500', desc: 'Received by inbox' },
        { label: 'Opened',    dot: 'bg-blue-500',    desc: 'Customer read the email' },
        { label: 'Clicked',   dot: 'bg-indigo-500',  desc: 'Customer clicked a link' },
        { label: 'Bounced',   dot: 'bg-red-500',     desc: 'One-click resend available' },
        { label: 'Dropped',   dot: 'bg-zinc-400',    desc: 'Rejected before send' },
      ],
      smsBadges: [
        { label: 'Delivered',   dot: 'bg-emerald-500', desc: 'Carrier confirmed delivery' },
        { label: 'Sent',        dot: 'bg-blue-500',    desc: 'Handed to carrier' },
        { label: 'Queued',      dot: 'bg-amber-500',   desc: 'Awaiting carrier accept' },
        { label: 'Undelivered', dot: 'bg-red-500',     desc: 'Carrier returned an error code' },
        { label: 'No SMS',      dot: 'bg-zinc-400',    desc: 'No phone captured' },
      ],

      topSalespersons: [
        { name: 'Sarah Mitchell', dealer: 'Astoria GWM',                count: 87 },
        { name: "James O'Connor", dealer: 'McRae Motors',              count: 74 },
        { name: 'Priya Sharma',   dealer: 'GWS Kia',                    count: 68 },
        { name: 'Marcus Bell',    dealer: 'Garry Warren Smith Honda',   count: 59 },
        { name: 'Daniel Tran',    dealer: 'Waverley Deepal',            count: 47 },
        { name: 'Aisha Khan',     dealer: 'Frankston Ford',             count: 41 },
      ],

      deliveryTimeline: [
        { type: 'Photo captured',         desc: 'Sales staff snaps the handover on phone or tablet',                tone: 'blue' },
        { type: 'Baseplate applied',      desc: 'Branded background composited around the photo automatically',     tone: 'blue' },
        { type: 'License plate blurred',  desc: 'Detected and blurred server-side before anyone else sees it',      tone: 'blue' },
        { type: 'Original archived',      desc: 'Both the raw and finished frames are kept on durable storage',     tone: 'blue' },
        { type: 'Email and SMS sent',     desc: 'Welcome message dispatched on both channels in parallel',          tone: 'green' },
        { type: 'Posted to socials',      desc: 'Auto-published to the dealer\'s Facebook and Instagram with the consented caption', tone: 'green' },
        { type: 'Review request sent',    desc: 'Customer prompted to leave a Google review with a one-tap link',   tone: 'green' },
        { type: 'Engagement tracked',     desc: 'Opens, clicks, social reach and review submissions stream into the dashboard', tone: 'green' },
      ],

      steps: [
        { icon: ICON.camera,   title: 'Capture',
          body: 'Sales staff snap a delivery photo on any phone or tablet. No app install, no customer logins.' },
        { icon: ICON.shield,   title: 'Auto-Privacy',
          body: 'License plates are detected automatically and blurred before the photo is stored.' },
        { icon: ICON.document, title: 'Consent + Story',
          body: 'A guided form captures customer name, story, salesperson, vehicle, and explicit publish consent.' },
        { icon: ICON.share,    title: 'Multi-Channel',
          body: 'Email and SMS go out together — both tracked end-to-end with live delivery status.' },
      ],

      reasons: [
        { icon: ICON.layers, title: 'Two-Stage Image Pipeline',
          body: 'Staff see the finished preview instantly; privacy blurring runs in the background so the floor never waits.' },
        { icon: ICON.gauge, title: 'Real-Time Engagement Tracking',
          body: 'Every open, click and delivery event lands in the dashboard the moment it happens — no batch delays.' },
        { icon: ICON.users, title: 'Built for Dealer Groups',
          body: 'A single admin view aggregates KPIs across every account in your group; staff see only their dealer.' },
        { icon: ICON.cloud, title: 'Durable Photo Archive',
          body: 'Original and finished copies of every capture are kept safely on durable cloud storage — replayable, exportable.' },
        { icon: ICON.lock, title: 'Role-Based Access',
          body: 'Staff, manager and admin roles, session management, and per-account scoping built in.' },
        { icon: ICON.chart, title: 'Scheduled Reports',
          body: 'Daily, weekly or monthly performance reports delivered straight to your inbox.' },
      ],

      form: { name: '', dealership: '', email: '', phone: '', message: '', website: '' },
      submitting: false,
      sent: false,
      error: null,
    };
  },

  computed: {
    marqueePhotos() {
      return [...this.allPhotos, ...this.allPhotos];
    },
    // Mosaic clusters — Square-style: each cluster is 3 photos arranged as
    // one tall portrait tile + a column of two stacked landscape tiles.
    // Cluster list is doubled so the CSS marquee scrolls seamlessly via
    // translateX(-50%).
    mosaicRowOne() { return this._buildClusters(0, 3); },
    chartPointsPro() {
      const w = 800, h = 300, padL = 48, padR = 12, padT = 30, padB = 30;
      const data = this.chartSeries;
      const max = Math.max(...data);
      const min = Math.min(...data);
      const span = Math.max(1, max - min);
      const stepX = (w - padL - padR) / (data.length - 1);
      return data.map((v, i) => ({
        x: padL + i * stepX,
        y: padT + (h - padT - padB) * (1 - (v - min) / span),
        v,
      }));
    },
    chartLinePathPro() {
      const pts = this.chartPointsPro;
      if (pts.length < 2) return '';
      let d = `M${pts[0].x.toFixed(1)},${pts[0].y.toFixed(1)}`;
      for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[i - 1] || pts[i];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[i + 2] || p2;
        const cp1x = p1.x + (p2.x - p0.x) / 6;
        const cp1y = p1.y + (p2.y - p0.y) / 6;
        const cp2x = p2.x - (p3.x - p1.x) / 6;
        const cp2y = p2.y - (p3.y - p1.y) / 6;
        d += ` C${cp1x.toFixed(1)},${cp1y.toFixed(1)} ${cp2x.toFixed(1)},${cp2y.toFixed(1)} ${p2.x.toFixed(1)},${p2.y.toFixed(1)}`;
      }
      return d;
    },
    chartAreaPathPro() {
      const pts = this.chartPointsPro;
      if (!pts.length) return '';
      const last = pts[pts.length - 1];
      const first = pts[0];
      return `${this.chartLinePathPro} L${last.x.toFixed(1)},270 L${first.x.toFixed(1)},270 Z`;
    },
    yAxisTicks() {
      const max = Math.max(...this.chartSeries);
      const min = Math.min(...this.chartSeries);
      const step = (max - min) / 4;
      return [4, 3, 2, 1, 0].map(i => Math.round(min + step * i));
    },
    chartTotal() { return this.chartSeries.reduce((a, b) => a + b, 0); },
    chartAvg()   { return Math.round(this.chartTotal / this.chartSeries.length); },
    chartPeak()  { return Math.max(...this.chartSeries); },
    chartWoW() {
      const n = this.chartSeries.length;
      const prev = this.chartSeries[n - 2];
      const last = this.chartSeries[n - 1];
      return Math.round(((last - prev) / prev) * 100);
    },
  },

  mounted() {
    this.loadAllPhotos();
    this._onScroll = this.updateStickyCta.bind(this);
    window.addEventListener('scroll', this._onScroll, { passive: true });
    this.updateStickyCta();

    // Pause the photo marquee during active resizing so the responsive width
    // breakpoints (sm: 640px) don't yank the translateX track sideways.
    this._onResize = () => {
      this.isResizing = true;
      clearTimeout(this._resizeSettle);
      this._resizeSettle = setTimeout(() => { this.isResizing = false; }, 250);
    };
    window.addEventListener('resize', this._onResize, { passive: true });
  },

  beforeDestroy() {
    if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    clearTimeout(this._resizeSettle);
  },

  methods: {
    startVideo() {
      // The facade swaps to the iframe on the visitor's click, but browsers gate
      // YouTube's autoplay param because the gesture isn't tied to the freshly
      // mounted frame — that's why the player used to need a *second* click.
      // Once the iframe fires @load, command playback explicitly via the JS API
      // (enablejsapi=1 in the src) so the first click reliably starts the video.
      const frame = this.$refs.videoIframe;
      if (!frame || !frame.contentWindow) return;
      frame.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: 'playVideo', args: [] }),
        '*'
      );
    },

    updateStickyCta() {
      // Show sticky pill once past the hero, hide it when the contact form
      // is in view (so we don't double up on the in-page CTA).
      const heroBottom = window.innerHeight * 0.6;
      const contact = document.getElementById('contact');
      const contactTop = contact ? contact.getBoundingClientRect().top : Infinity;
      this.showStickyCta = window.scrollY > heroBottom && contactTop > window.innerHeight * 0.4;
    },

    _buildClusters(offset, size) {
      // Cap how many photos the marquee actually renders. The track is doubled
      // for the seamless -50% loop, and each cluster is ~1120px wide, so the
      // composited track width = clusters × 2 × 1120px. WebKit drops the paint
      // on any compositing layer wider than ~16,384px (its max GPU texture
      // size) — the strip loads, then blanks as it scrolls, and only reappears
      // when DevTools forces a re-tile. Capping at 6 clusters keeps the doubled
      // track to ~13,400px, safely under the limit, while still giving a long,
      // varied loop. Extra photos beyond the cap simply aren't shown.
      const MAX_CLUSTERS = 6;
      const photos = this.allPhotos;
      if (!photos.length) return [];
      const clusters = [];
      const needed = Math.max(size, Math.min(photos.length, MAX_CLUSTERS * size));
      for (let i = 0; i < needed && clusters.length < MAX_CLUSTERS; i += size) {
        const cluster = [];
        for (let j = 0; j < size; j++) {
          cluster.push(photos[(offset + i + j) % photos.length]);
        }
        clusters.push(cluster);
      }
      // Double for seamless -50% scroll
      return [...clusters, ...clusters];
    },

    // Merge a batch of photo URLs from one source (a dealer slug, or 'samples')
    // into its own bucket, then rebuild allPhotos by interleaving the buckets
    // round-robin. Photos arrive grouped by dealer — i.e. by brand — so a plain
    // append would stack one brand's photos contiguously and the marquee would
    // show long runs of the same make. Round-robin interleaving guarantees that
    // when more than one brand is loaded, no two adjacent tiles share a brand.
    _addPhotos(source, urls) {
      if (!urls || !urls.length) return;
      // Dedupe within the source and against everything already buffered, so a
      // photo that appears under two dealers (or re-arrives) is only shown once.
      const seen = new Set();
      Object.values(this.photoSources).forEach(list => list.forEach(u => seen.add(u)));
      const existing = this.photoSources[source] || [];
      const fresh = urls.filter(u => u && !seen.has(u));
      if (!fresh.length) return;
      this.photoSources = { ...this.photoSources, [source]: [...existing, ...fresh] };
      this._rebuildPhotos();
      // First photos on screen — drop the skeleton immediately.
      if (this.photosLoading) this.photosLoading = false;
    },

    // Flatten the per-source buckets into a single brand-interleaved list.
    // Rather than plain round-robin (which trails the longest bucket's surplus
    // as a single-brand run at the end), each bucket's photos are spread evenly
    // across the whole timeline by a fractional "next position" cursor, then the
    // merged list is ordered by that position. This keeps brands maximally
    // separated even when dealers return very different photo counts. Buckets
    // are visited in a stable session order so the sequence doesn't reshuffle as
    // later batches stream in.
    _rebuildPhotos() {
      const keys = Object.keys(this.photoSources).filter(k => this.photoSources[k].length);
      if (!keys.length) { this.allPhotos = []; return; }
      // Establish a stable visit order once, the first time we have ≥1 source.
      if (!this._sourceOrder) this._sourceOrder = [];
      keys.forEach(k => { if (!this._sourceOrder.includes(k)) this._sourceOrder.push(k); });
      const order = this._sourceOrder.filter(k => this.photoSources[k] && this.photoSources[k].length);

      // Place each photo at a normalised position in [0, 1): a bucket with N
      // photos puts its i-th photo at (i + 0.5) / N, so its photos are evenly
      // fanned out across the strip. Ties (same position from two brands) break
      // by the stable source order, never clustering the same brand together.
      const placed = [];
      order.forEach((k, srcIdx) => {
        const list = this.photoSources[k];
        const n = list.length;
        list.forEach((url, i) => {
          placed.push({ url, pos: (i + 0.5) / n, srcIdx });
        });
      });
      placed.sort((a, b) => (a.pos - b.pos) || (a.srcIdx - b.srcIdx));

      // The marquee track is doubled (clusters + clusters) and loops, so the
      // last tile sits next to the first at the wrap point. If both ends are the
      // same brand, rotate the list so the seam lands on a brand boundary.
      if (order.length > 1 && placed.length > 1 &&
          placed[0].srcIdx === placed[placed.length - 1].srcIdx) {
        const firstSrc = placed[0].srcIdx;
        const cut = placed.findIndex(p => p.srcIdx !== firstSrc);
        if (cut > 0) placed.push(...placed.splice(0, cut));
      }
      this.allPhotos = placed.map(p => p.url);
    },

    async loadAllPhotos() {
      // 1) Paint the local sample photos first — they're static assets, so
      //    they're effectively instant and give the marquee something to show
      //    while the live dealer requests are still in flight.
      axios
        .get('/assets/delivery-samples/manifest.json', { timeout: 4000 })
        .then(res => Array.isArray(res.data && res.data.photos) ? res.data.photos : [])
        .then(names => names.map(n => `/assets/delivery-samples/${n}`))
        .catch(() => [])
        .then(samples => {
          this.samplePhotos = samples;
          this._addPhotos('samples', samples);
        });

      // 2) For each showcase dealer, check it's active then stream its live
      //    photos in independently. No Promise.all barrier — a slow dealer no
      //    longer holds up every other dealer (or the manifest).
      const cacheBust = Date.now();
      const dealerLoads = this.showcaseSlugs.map(async slug => {
        let active = false;
        try {
          const res = await axios.get(
            `/.netlify/functions/check-account-active?slug=${encodeURIComponent(slug)}`,
            { timeout: 6000 }
          );
          active = res.data && res.data.active === true && res.data.reason !== 'check_failed';
        } catch (e) {
          active = false;
        }
        if (!active) return;
        this.activeSlugs.push(slug);

        // Fetch up to 10 per slug — server-side quality filter on
        // recent-delivery-photos.js drops anonymous / off-domain /
        // no-vehicle / no-salesperson entries before we ever see them.
        try {
          const res = await axios.get(
            `/.netlify/functions/recent-delivery-photos?slug=${encodeURIComponent(slug)}&limit=10&nocache=1&_=${cacheBust}`,
            { timeout: 8000 }
          );
          const photos = (res.data && Array.isArray(res.data.photos)) ? res.data.photos : [];
          this._addPhotos(slug, photos.map(p => (typeof p === 'string' ? p : p && p.url)).filter(Boolean));
        } catch (e) {
          /* one dealer failing must not blank the marquee */
        }
      });

      // Once every dealer has settled, clear the skeleton even if nothing
      // came back (so we don't spin the pulse forever on an empty network).
      Promise.all(dealerLoads).finally(() => { this.photosLoading = false; });
    },

    rankBadgeClasses(index) {
      const base = 'w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold tabular-nums';
      switch (index) {
        case 0: return `${base} bg-gradient-to-br from-amber-300 to-amber-500 text-amber-950 ring-1 ring-amber-300/40 shadow-md shadow-amber-500/20`;
        case 1: return `${base} bg-gradient-to-br from-zinc-300 to-zinc-400 text-zinc-900 ring-1 ring-zinc-300/40 shadow-md shadow-zinc-400/20`;
        case 2: return `${base} bg-gradient-to-br from-orange-400 to-orange-500 text-orange-950 ring-1 ring-orange-400/40 shadow-md shadow-orange-500/20`;
        default: return `${base} bg-white text-[#86868b] ring-1 ring-[#e8e8ed]`;
      }
    },

    timelineDotClasses(tone) {
      switch (tone) {
        case 'green': return 'text-emerald-600 bg-emerald-50 border border-emerald-200';
        case 'blue':  return 'text-[#0071e3] bg-blue-50 border border-blue-200';
        default:      return 'text-[#86868b] bg-[#f5f5f7] border border-[#e8e8ed]';
      }
    },

    sparkLinePath(series) {
      const w = 100, h = 24;
      const max = Math.max(...series);
      const min = Math.min(...series);
      const span = Math.max(1, max - min);
      const stepX = w / (series.length - 1);
      return series.map((v, i) => {
        const x = i * stepX;
        const y = h - (h - 4) * ((v - min) / span) - 2;
        return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
      }).join(' ');
    },
    sparkAreaPath(series) {
      return `${this.sparkLinePath(series)} L100,24 L0,24 Z`;
    },

    async submit() {
      this.submitting = true;
      this.error = null;
      try {
        await axios.post('/.netlify/functions/request-contact', {
          name: this.form.name,
          dealership: this.form.dealership,
          email: this.form.email,
          phone: this.form.phone,
          message: this.form.message,
          website: this.form.website,
          pageUrl: typeof window !== 'undefined' ? window.location.href : '',
        });
        this.sent = true;
      } catch (e) {
        this.error = e.response?.data?.error || 'Could not send your enquiry. Please try again or email us at advertising@adme.net.au.';
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
/* ═══════════════════════════════════════════════════════════════════
   ADME landing-page design tokens — light theme, Inter, Apple-blue,
   alternating section surfaces, restrained shadows.
   ═══════════════════════════════════════════════════════════════════ */
/* Inter is preloaded in public/index.html via <link rel="stylesheet"> so
   it loads asynchronously without blocking first paint. The Vue scoped
   @import variant breaks on mobile Safari and was causing the splash to
   appear blank on phones. */

.home-draft-root {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  letter-spacing: -0.011em;
  color: #1d1d1f;
  font-feature-settings: "cv11", "ss01";
}

/* ── Nav ── */
.adme-nav {
  position: sticky;
  top: 0;
  z-index: 50;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.adme-nav-link {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 500;
  color: #1d1d1f;
  transition: background 180ms ease, color 180ms ease;
}
.adme-nav-link:hover { background: rgba(0, 0, 0, 0.04); }

/* ── Typography ── */
.adme-eyebrow {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #0071e3;
}
.adme-display {
  font-weight: 600;
  letter-spacing: -0.04em;
  line-height: 1.05;
  color: #1d1d1f;
}
.adme-display.adme-display-light {
  color: #ffffff;
  text-shadow: 0 2px 24px rgba(0, 0, 0, 0.25);
}
.adme-h2 {
  font-weight: 600;
  letter-spacing: -0.03em;
  line-height: 1.08;
  color: #1d1d1f;
}
.adme-h2-dark {
  font-weight: 600;
  letter-spacing: -0.03em;
  line-height: 1.08;
  background: linear-gradient(180deg, #ffffff 0%, #d2d2d7 100%);
  -webkit-background-clip: text;
          background-clip: text;
  color: transparent;
}
.adme-lede {
  color: #6e6e73;
  font-weight: 400;
  line-height: 1.55;
  letter-spacing: -0.005em;
}

/* ── Chips / dots ── */
.adme-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 9999px;
  font-size: 13px;
  background: rgba(0, 0, 0, 0.04);
  color: #1d1d1f;
}
.adme-chip.adme-chip-dark {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(8px);
}
.adme-range-chip {
  display: inline-flex;
  align-items: center;
  padding: 5px 11px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 500;
  color: #6e6e73;
  background: rgba(0, 0, 0, 0.04);
  transition: all 180ms ease;
}
.adme-range-chip.is-active {
  background: #1d1d1f;
  color: #ffffff;
}
.adme-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 9999px;
}
.adme-dot-emerald {
  background: #10b981;
  box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.6);
  animation: adme-pulse 2s ease-in-out infinite;
}
@keyframes adme-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.5); }
  50%      { box-shadow: 0 0 0 6px rgba(16, 185, 129, 0); }
}

/* ── Cards ── */
.adme-card {
  background: #ffffff;
  border: 1px solid #e8e8ed;
  border-radius: 22px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: transform 300ms ease, box-shadow 300ms ease, border-color 300ms ease;
}
.adme-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 32px -8px rgba(0, 0, 0, 0.08);
  border-color: #d2d2d7;
}

/* ── Icon tile (light) ── */
.adme-icon-tile {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(0, 113, 227, 0.08);
}

/* ── Buttons ── */
.adme-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 11px 22px;
  border-radius: 9999px;
  font-weight: 500;
  letter-spacing: -0.005em;
  font-size: 15px;
  transition: all 180ms ease;
  white-space: nowrap;
}
.adme-btn-primary {
  background: #0071e3;
  color: #ffffff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}
.adme-btn-primary:hover {
  background: #0077ed;
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(0, 113, 227, 0.25);
}
.adme-btn-link {
  background: transparent;
  color: #0071e3;
  padding: 11px 14px;
}
.adme-btn-link:hover {
  color: #0077ed;
}

/* ── Engagr in-flight orbital ──
   Reuses the form-submit loader motif on the homepage to suggest
   messages in flight. Five dots scale up/down on a staggered cycle
   while the whole assembly rotates slowly. */
.engagr-orbital {
  position: relative;
  width: 320px;
  height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.engagr-orbital-logo {
  position: absolute;
  width: 200px;
  height: auto;
  z-index: 2;
  filter: drop-shadow(0 0 24px rgba(159, 191, 255, 0.2));
}
.engagr-orbital-dots {
  position: absolute;
  width: 100%;
  height: 100%;
  animation: engagr-orbital-spin 18s linear infinite;
}
.engagr-orbital-dot {
  position: absolute;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #9fbfff;
  box-shadow: 0 0 12px rgba(159, 191, 255, 0.6);
  animation: engagr-orbital-pulse 2.4s ease-in-out infinite;
}
.engagr-orbital-dot:nth-child(1) { top: 0;    left: 50%; transform: translateX(-50%); animation-delay: 0s; }
.engagr-orbital-dot:nth-child(2) { top: 25%;  right: 5%;                              animation-delay: 0.48s; }
.engagr-orbital-dot:nth-child(3) { bottom: 15%; right: 18%;                           animation-delay: 0.96s; }
.engagr-orbital-dot:nth-child(4) { bottom: 15%; left: 18%;                            animation-delay: 1.44s; }
.engagr-orbital-dot:nth-child(5) { top: 25%;  left: 5%;                               animation-delay: 1.92s; }

@keyframes engagr-orbital-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}
@keyframes engagr-orbital-pulse {
  0%, 100% { transform: scale(0.4); opacity: 0.5; }
  50%      { transform: scale(1);   opacity: 1; }
}
.engagr-orbital-dot:nth-child(1) { transform: translateX(-50%); }
.engagr-orbital-dot:nth-child(1) { animation-name: engagr-orbital-pulse-top; }
@keyframes engagr-orbital-pulse-top {
  0%, 100% { transform: translateX(-50%) scale(0.4); opacity: 0.5; }
  50%      { transform: translateX(-50%) scale(1);   opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .engagr-orbital-dots,
  .engagr-orbital-dot { animation: none; }
  .engagr-orbital-dot { opacity: 0.8; transform: scale(0.7); }
  .engagr-orbital-dot:nth-child(1) { transform: translateX(-50%) scale(0.7); }
}

/* ── Sticky CTA pill ── */
.adme-sticky-cta {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 60;
  display: inline-flex;
  align-items: center;
  padding: 12px 20px;
  border-radius: 9999px;
  background: #0071e3;
  color: #ffffff;
  font-weight: 500;
  font-size: 14px;
  letter-spacing: -0.005em;
  box-shadow: 0 8px 24px rgba(0, 113, 227, 0.35), 0 2px 6px rgba(0, 0, 0, 0.1);
  transition: transform 180ms ease, box-shadow 180ms ease, background 180ms ease;
  white-space: nowrap;
}
.adme-sticky-cta:hover {
  background: #0077ed;
  transform: translateY(-2px);
  box-shadow: 0 12px 28px rgba(0, 113, 227, 0.4), 0 4px 10px rgba(0, 0, 0, 0.12);
}
.sticky-cta-enter-active,
.sticky-cta-leave-active {
  transition: opacity 220ms ease, transform 220ms ease;
}
.sticky-cta-enter,
.sticky-cta-leave-to {
  opacity: 0;
  transform: translateY(12px);
}
@media (max-width: 640px) {
  .adme-sticky-cta {
    bottom: 16px;
    right: 16px;
    padding: 10px 16px;
    font-size: 13px;
  }
}

/* ── Inputs ── */
.adme-label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #6e6e73;
  margin-bottom: 8px;
  letter-spacing: -0.005em;
}
.adme-input {
  width: 100%;
  background: #ffffff;
  border: 1px solid #d2d2d7;
  border-radius: 12px;
  padding: 12px 14px;
  color: #1d1d1f;
  font-size: 15px;
  transition: all 180ms ease;
  font-family: inherit;
}
.adme-input::placeholder { color: #aeaeb2; }
.adme-input:focus {
  outline: none;
  border-color: #0071e3;
  box-shadow: 0 0 0 4px rgba(0, 113, 227, 0.15);
}

/* ── Dark accent section ── */
.dark-accent {
  background: linear-gradient(180deg, #1d1d1f 0%, #2a2a2c 100%);
  color: #f5f5f7;
}
.adme-card-dark {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 22px;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  transition: transform 300ms ease, background 300ms ease;
}
.adme-card-dark:hover {
  transform: translateY(-2px);
  background: rgba(255, 255, 255, 0.06);
}
.adme-icon-tile-dark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(159, 191, 255, 0.12);
  border: 1px solid rgba(159, 191, 255, 0.18);
}
.adme-status-row-dark {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
}

/* ── Misc ── */
.tabular-nums { font-variant-numeric: tabular-nums; }

/* ── Marquee — copied from AccountDeactivated.vue ── */
/* ───── Footer + mega wordmark ───── */
.adme-footer {
  background: #0a0a0c;
  color: #ffffff;
  margin-top: 0;
  margin-bottom: 0;
  padding-bottom: 0;
  overflow: hidden;
}
.adme-mega-wordmark {
  display: block;
  width: 100%;
  text-align: center;
  font-family: 'Inter', -apple-system, system-ui, sans-serif;
  font-weight: 700;
  letter-spacing: 0.01em;
  line-height: 0.78;
  font-size: clamp(7rem, 28vw, 26rem);
  color: #ffffff;
  text-decoration: none;
  cursor: pointer;
  /* Pull the wordmark up so the baseplate (site footer) sits closer to
     the middle of the glyphs — the Podium / Square move. Top pull raises
     the whole block; bottom pull keeps descenders tucking under the
     footer band rather than leaving a gap above it. */
  margin-top: 0;
  margin-bottom: -0.18em;
  padding-top: 0;
  user-select: none;
  white-space: nowrap;
}
.adme-mega-letter {
  display: inline-block;
  transform-origin: 50% 100%;
  transition: transform 500ms cubic-bezier(0.16, 1, 0.3, 1), color 200ms ease;
  will-change: transform;
}
.adme-mega-letter:hover {
  transform: scale(1.15);
  color: #ffffff;
}
.sr-only {
  position: absolute;
  width: 1px; height: 1px;
  padding: 0; margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
@media (prefers-reduced-motion: reduce) {
  .adme-mega-letter { transition: none; }
  .adme-mega-letter:hover { transform: none; }
}

.photo-marquee {
  position: relative;
  /* NOTE: do NOT use `mask-image`/`-webkit-mask-image` here. On WebKit, a CSS
     mask over a child running a `transform` animation intermittently fails to
     paint — tiles load, then vanish, and only reappear after a forced repaint
     (opening DevTools, scrolling, resizing). The faded edges are instead drawn
     as solid gradient overlays (::before/::after below), which Safari
     composites reliably. Likewise no `contain` / `will-change` /
     `backface-visibility` on the animated track — those layer-promotion hints
     are what Safari was dropping. */
}
/* Faded left/right edges, painted as opaque white→transparent overlays on top
   of the strip instead of a CSS mask. Section background is white, so these
   blend seamlessly. */
.photo-marquee::before,
.photo-marquee::after {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  width: 6%;
  z-index: 1;
  pointer-events: none;
}
.photo-marquee::before {
  left: 0;
  background: linear-gradient(to right, #ffffff, rgba(255, 255, 255, 0));
}
.photo-marquee::after {
  right: 0;
  background: linear-gradient(to left, #ffffff, rgba(255, 255, 255, 0));
}
/* The animated track must live on its own stable GPU compositing layer.
   On WebKit, a marquee animated with a bare 2D `translateX` lets Safari fall
   back to CPU painting as the strip scrolls — tiles load, then blank out once
   they pass the layer's origin (the "load slowly then disappear" symptom).
   Forcing a 3D layer (translateZ + 3D keyframes + backface-visibility) keeps
   the whole strip rasterized as one unit that simply slides, so nothing is
   re-painted mid-scroll and tiles never vanish. */
.photo-marquee-track {
  will-change: transform;
  transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
}
/* While the user is actively resizing, pause the marquee so a width-driven
   translate change doesn't jerk the photos sideways. The .is-resizing class is
   toggled by the resize handler in Welcome.vue. */
.photo-marquee.is-resizing .photo-marquee-track {
  animation-play-state: paused;
}
/* Single row. Animated with translate3d (not translateX) so WebKit keeps the
   track on the GPU for the whole loop. */
.photo-marquee-row-1 { animation: photo-marquee-scroll 240s linear infinite; }

@keyframes photo-marquee-scroll {
  from { transform: translate3d(0, 0, 0); }
  to   { transform: translate3d(-50%, 0, 0); }
}
@keyframes photo-marquee-scroll-reverse {
  from { transform: translate3d(-50%, 0, 0); }
  to   { transform: translate3d(0, 0, 0); }
}
@media (prefers-reduced-motion: reduce) {
  .photo-marquee-track { animation: none; }
}

/* ── Honeypot ── */
.hp-field {
  position: absolute;
  left: -10000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
}

/* ── Smooth scroll ── */
html { scroll-behavior: smooth; }

/* White page background — fills any host-page whitespace below the
   engagr footer so the area outside this component reads as plain white
   rather than a dark gap. */
body { background: #0a0a0c; }
.home-draft-root { background: #ffffff; }

/* ── Footer background animation — matches /dashboard ── */
@keyframes subtle-zoom {
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.05); }
}
.bg-image-animate {
  animation: subtle-zoom 30s ease-in-out infinite;
}
</style>
